import clsx from 'clsx'
import { orderBy } from 'lodash-es'
import type { ParsedQuery } from 'query-string'
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FvButton } from '@fv/client-components'
import { fvDate } from '@fv/dates'
import type { ShipmentListPreference } from '@fv/models'

import { ScheduledReportsControls } from '@/components/ScheduledReportsControls'

import { useCalendarWeekShipments } from '../shipment-calendar/api'
import { CalendarDayItem } from '../shipment-calendar/CalendarDayItem'
import { CalendarDayPanel } from '../shipment-calendar/CalendarDayPanel'
import {
  useBucketsByDate,
  useCalendar,
  useShipmentsWithEntries,
} from '../shipment-calendar/hooks'
import {
  buildWeekLookupDTO,
  parseCalendarDateKey,
  toCalendarDateKey,
} from '../shipment-calendar/utils'
import { CompactFilterBar } from '../shipment-filters/CompactFilterBar'
import { ShipmentFilterPanel } from '../shipment-filters/FilterPanel'
import { useHandleFilterChange } from '../shipment-filters/hooks'
import { useConfirmedListParams } from '../shipment-list/hooks/useShipmentListParams'
import { ShipmentViewSelect } from '../shipment-list/ShipmentViewSelect'

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

type Props = {
  onViewChange: (view: ShipmentListPreference) => void
}

export const CalendarShipmentList = ({ onViewChange }: Props) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    fvDate().toDate(),
  )
  const selectedDateKey = useMemo(
    () => (selectedDate ? toCalendarDateKey(selectedDate) : null),
    [selectedDate],
  )
  const anchorDate = selectedDate ?? fvDate().toDate()
  const calendar = useCalendar(anchorDate)
  const { cells, rows, weeks } = calendar

  const { qs } = useConfirmedListParams()
  const { handle: applyFilterToQuery } = useHandleFilterChange(listRef)
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false)
  const [isScheduleOpen, setScheduleOpen] = useState(false)

  const weekFilters = useMemo(() => {
    return weeks.map(week => ({
      weekKey: week.key,
      filter: buildWeekLookupDTO(qs, week.start, week.end),
    }))
  }, [weeks, qs])
  const query = useCalendarWeekShipments(weekFilters)
  const summaries = query.data ?? []
  const shipments = useShipmentsWithEntries(summaries)
  const bucketsByDate = useBucketsByDate(shipments)
  const search = new URLSearchParams(qs).get('search')
  const prevSearchRef = useRef<string | null>(null)

  const todayKey = useMemo(() => toCalendarDateKey(fvDate().toDate()), [])

  useEffect(() => {
    if (query.isLoading) return
    if (!search || search === prevSearchRef.current || shipments.length === 0) {
      prevSearchRef.current = search
      return
    }
    prevSearchRef.current = search

    const latestKey = orderBy(
      shipments.flatMap(s => s.entries),
      e => e.dateKey,
      'desc',
    )[0]?.dateKey

    if (latestKey) {
      setSelectedDate(parseCalendarDateKey(latestKey))
    }
  }, [search, shipments, query.isLoading])

  const handleFilterChange = useCallback(
    (data: ParsedQuery) => {
      applyFilterToQuery({ ...data })
    },
    [applyFilterToQuery],
  )

  const handleGridKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const isLeft = event.key === 'ArrowLeft'
      const isRight = event.key === 'ArrowRight'
      const isUp = event.key === 'ArrowUp'
      const isDown = event.key === 'ArrowDown'
      if (!isLeft && !isRight && !isUp && !isDown) {
        return
      }
      setIsKeyboardNavigating(true)
      event.preventDefault()
      let inc = 0
      if (isLeft) {
        inc = -1
      }
      if (isRight) {
        inc = 1
      }
      if (isUp) {
        inc = -7
      }
      if (isDown) {
        inc = 7
      }
      if (inc) {
        const base = selectedDate
        setSelectedDate(fvDate(base).add(inc, 'day').toDate())
      }
    },
    [selectedDate],
  )

  useEffect(() => {
    if (!isKeyboardNavigating || !selectedDate) return
    const focusKey = toCalendarDateKey(selectedDate)
    const focusButton = listRef.current?.querySelector<HTMLButtonElement>(
      `button[data-date-key="${focusKey}"]`,
    )
    focusButton?.focus()
  }, [selectedDate, isKeyboardNavigating])

  return (
    <div className="grid overflow-hidden grid-cols-[19rem_1fr_31rem] h-full">
      <ShipmentFilterPanel
        className={clsx(
          'b1400:col-start-1 b1400:row-start-1',
          'col-start-1 row-start-1',
        )}
        drawerAt="always"
        onChange={handleFilterChange}
        onScheduleOpen={() => setScheduleOpen(true)}
      />

      <div
        className="bg-fv-gray-50 overflow-auto px-6 py-8 flex flex-col relative z-10 col-start-1 col-span-2 row-start-1"
        ref={listRef}
      >
        <nav className="mb-6 fa-width-auto flex items-center gap-2 b1350:flex-wrap">
          <ShipmentViewSelect
            value="calendar"
            onChange={onViewChange}
            className="b1350:w-full"
            selectClassName="b1350:w-full"
          />
          <CompactFilterBar onChange={handleFilterChange} />
        </nav>
        <div className="flex flex-col flex-1 relative">
          <div className="absolute -top-4 b1300:-top-6 z-30 b1400:-right-2 flex right-2 bg-fv-beer-xlight rounded-full p-1 border-2 border-fv-beer-medium">
            <FvButton
              theme="round"
              icon="chevron-left"
              aria-label="Previous month"
              onClick={() =>
                setSelectedDate(fvDate(anchorDate).add(-1, 'month').toDate())
              }
              className="!w-3 !h-3 !p-3"
            />

            <FvButton
              theme="round"
              icon="chevron-right"
              aria-label="Next month"
              onClick={() =>
                setSelectedDate(fvDate(anchorDate).add(1, 'month').toDate())
              }
              className="!w-3 !h-3 !p-3"
            />
          </div>
          <div className="grid grid-cols-7 uppercase font-medium">
            {weekDays.map(day => (
              <div
                key={day}
                className={clsx(
                  'border border-fv-blue-200 bg-fv-blue-50 p-2 text-sm tracking-widest',
                )}
              >
                {day}
              </div>
            ))}
          </div>
          <div
            className="grid flex-1 grid-cols-7"
            style={{
              gridTemplateRows: `repeat(${rows}, minmax(max-content, 1fr))`,
            }}
            onPointerMove={() => {
              if (isKeyboardNavigating) {
                setIsKeyboardNavigating(false)
              }
            }}
          >
            {cells.map(cell => {
              const cellKey = toCalendarDateKey(cell.date)

              return (
                <CalendarDayItem
                  key={cellKey}
                  dayOfMonth={cell.dayOfMonth}
                  monthLabel={fvDate(cell.date).format('MMM YYYY')}
                  isToday={cellKey === todayKey}
                  inCurrentMonth={cell.isCurrentMonth}
                  isSelected={selectedDateKey === cellKey}
                  dataDateKey={cellKey}
                  dayBuckets={bucketsByDate.get(cellKey) ?? []}
                  isKeyboardNavigating={isKeyboardNavigating}
                  onSelect={() => setSelectedDate(cell.date)}
                  onKeyDown={handleGridKeyDown}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating) {
                      setIsKeyboardNavigating(false)
                    }
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>

      <div className="bg-fv-blue-50 border-l border-fv-blue-200 flex flex-col overflow-hidden">
        <CalendarDayPanel
          date={selectedDate}
          shipments={shipments}
          onDateSelected={setSelectedDate}
        />
      </div>

      <ScheduledReportsControls
        isOpen={isScheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />
    </div>
  )
}
